import { firestore } from "@/services/firestore";
import { createConverter } from "@/repositories/common";
import { ContainerTimeConfig, MetricsConfigs } from "@/types/v2/Metrics";
import { authActions } from "@/services/auth";

// Since MetricsConfigs is a new type, we don't need V1/V2 conversion
const metricsTransformer = {
  isV1: (_data: MetricsConfigs): _data is MetricsConfigs => false, // Always false since this is new
  transformV1ToV2: (data: MetricsConfigs): MetricsConfigs => data,
};

const metricsConverter = createConverter<MetricsConfigs, MetricsConfigs>(
  metricsTransformer
);

export const metrics = {
  // Get metrics configurations for a shop
  getConfigs: async (shopId: string) => {
    return firestore.getDocument<MetricsConfigs>(
      `shops/${shopId}/metrics/configs`,
      metricsConverter
    );
  },

  // Save metrics configurations for a shop
  saveConfigs: async (shopId: string, configs: MetricsConfigs) => {
    try {
      // Try to update first
      return await firestore.updateDocument(
        `shops/${shopId}/metrics/configs`,
        configs
      );
    } catch (error) {
      // If document doesn't exist, create it
      return await firestore.createDocument(
        `shops/${shopId}/metrics/configs`,
        configs,
        metricsConverter
      );
    }
  },

  // Add a new container time configuration
  addContainerTimeConfig: async (
    shopId: string,
    config: Omit<
      ContainerTimeConfig,
      "id" | "createdAt" | "updatedAt" | "createdBy"
    >
  ) => {
    const currentUser = authActions.currentUser();
    if (!currentUser) {
      throw new Error("No current user found");
    }

    const newConfig: ContainerTimeConfig = {
      ...config,
      id: Math.random().toString(36).substring(7), // Simple ID generation
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: currentUser.uid,
    };

    // Get existing configs
    const existingConfigs = await metrics.getConfigs(shopId);
    const currentConfigs = existingConfigs || { containerTimeConfigs: [] };

    // Add new config
    const updatedConfigs: MetricsConfigs = {
      containerTimeConfigs: [...currentConfigs.containerTimeConfigs, newConfig],
    };

    // Save updated configs
    await metrics.saveConfigs(shopId, updatedConfigs);
    return newConfig;
  },

  // Update an existing container time configuration
  updateContainerTimeConfig: async (
    shopId: string,
    configId: string,
    updates: Partial<
      Omit<ContainerTimeConfig, "id" | "createdAt" | "createdBy">
    >
  ) => {
    const currentUser = authActions.currentUser();
    if (!currentUser) {
      throw new Error("No current user found");
    }

    // Get existing configs
    const existingConfigs = await metrics.getConfigs(shopId);
    if (!existingConfigs) {
      throw new Error("No configurations found");
    }

    // Find and update the config
    const updatedConfigs: MetricsConfigs = {
      containerTimeConfigs: existingConfigs.containerTimeConfigs.map(
        (config) =>
          config.id === configId
            ? {
                ...config,
                ...updates,
                updatedAt: new Date(),
              }
            : config
      ),
    };

    // Save updated configs
    await metrics.saveConfigs(shopId, updatedConfigs);
    return updatedConfigs.containerTimeConfigs.find((c) => c.id === configId);
  },

  // Delete a container time configuration
  deleteContainerTimeConfig: async (shopId: string, configId: string) => {
    // Get existing configs
    const existingConfigs = await metrics.getConfigs(shopId);
    if (!existingConfigs) {
      throw new Error("No configurations found");
    }

    // Remove the config
    const updatedConfigs: MetricsConfigs = {
      containerTimeConfigs: existingConfigs.containerTimeConfigs.filter(
        (config) => config.id !== configId
      ),
    };

    // Save updated configs
    await metrics.saveConfigs(shopId, updatedConfigs);
  },
};
